<template xmlns:v-slot="http://www.w3.org/1999/XSL/Transform">
  <v-navigation-drawer
    :width="getNavWidth()"
    :height="getNavHeight()"
    :value="(isCheckoutPageOnDesktop || showCart) && !disabledCart"
    :temporary="!isCheckoutPageOnDesktop"
    :app="!isCheckoutPageOnDesktop"
    :disable-resize-watcher="isCheckoutPageOnDesktop"
    :permanent="(isCheckoutPageOnDesktop && !disabledCart) || navPermanent"
    right
    absolute
    clipped
    overflow
    @input="toggleCart">
    <SideContent :class="$style.cartContent">
      <template v-slot:header>
        <button :class="$style.pastOrdersClose">
          {{ isCheckoutPageOnDesktop ? 'Order Details' : 'Your Cart' }}
        </button>

        <div :class="$style.cartTitle">
          <div :class="$style.cartTitleLogo">
            <Logo/>
          </div>
          <h4 :class="$style.cartMainTitle"><span>Review Your Order</span></h4>
          <BaseButton :class="$style.closeDialog"
                      button-style="Back"
                      @click="setNavPermanent(false),toggleCart(false)">
            <svg v-svg
                 :class="$style.backIcon"
                 symbol="straight-arrow"/>
            <span>Back</span>
          </BaseButton>
        </div>

      </template>
      <template v-slot:main>
        <div :class="$style.currentOrderCart">
          <div :class="$style.currentOrderItems">
            <CartProduct v-for="(value, key) in cart"
                         :key="key"
                         :order-item-id="key"
                         :disabled="isCheckoutPageOnDesktop"
                         :class="$style.currentOrderItem"
                         @change-quantity="changeQuantity(key, $event)"
                         @edit="handleEditItem"/>
          </div>
        </div>
      </template>
      <template v-slot:footer>
        <CartTotal v-if="currentPage !== 'checkout' || isDesktopResolution"
                   :current-page="currentPage"
                   @add-item="handleAddItem"
                   @start-over="handleStartOver"
                   @checkout="checkout"/>
      </template>
    </SideContent>
  </v-navigation-drawer>
</template>
<script>
import Component from 'vue-class-component';
import Vue from 'vue';
import { Action, Getter } from 'vuex-class';
import { debounce } from 'debounce';
import {
  CART_DISPLAY,
  CART_ITEM_SET_QUANTITY,
  CART_ITEM_REMOVE,
  CART_CALC_ORDER,
  CART_PREPARE_ORDER,
  ORDER_PATCH,
} from '@/apps/cart/store/types';
import {
  CONFIGURATOR_PRODUCT,
  CONFIGURATOR_CLEAR,
} from '@/apps/configurator/store/types';
import { MODAL_UPDATE, API_ERROR_SET } from '@/apps/core/store/types';
import { DEBOUNCE_ORDER_QUANTITY_CALC, KIOSK_BREAKPOINT } from '@/config/constants';
import SideContent from '@/layouts/SideContent';
import BaseButton from '@/components/BaseButton';
import Logo from '@/components/Logo';
import CartProduct from '@/apps/cart/components/CartProduct';
import CartTotal from '@/apps/cart/components/CartTotal';

const DESKTOP_RESOLUTION = 1081;

/**
 * Base container for cart data. Provide navigation drawer configuration and
 * cart products data rendering inside side content component.
 */
@Component({
  components: {
    BaseButton,
    CartTotal,
    CartProduct,
    SideContent,
    Logo,
  },
  props: {
    currentPage: '',
  },
  watch: {
    /**
     * Cart property watcher
     * @param value Cart new value
     */
    cart(value) {
      if (Object.keys(value).length === 0) {
        this.setNavPermanent(false);
        this.toggleCart(false);
      }
    },
  },
})
export default class Cart extends Vue {
  @Action(CART_DISPLAY) cartDisplay;
  @Action(CART_ITEM_SET_QUANTITY) cartItemSetQuantity;
  @Action(CONFIGURATOR_PRODUCT) setCurrentProduct;
  @Action(MODAL_UPDATE) modalUpdate;
  @Action(CART_ITEM_REMOVE) removeOrderItem;
  @Action(CONFIGURATOR_CLEAR) clearConfigurator;
  @Action(CART_CALC_ORDER) cartCalcOrder;
  @Action(CART_PREPARE_ORDER) cartPrepareOrder;
  @Action(ORDER_PATCH) updateOrder;
  @Action(API_ERROR_SET) setApiError;
  @Getter cart;
  @Getter showCart;
  @Getter authUser;
  @Getter orderItemPrice;
  @Getter restaurantId;
  @Getter orderType;
  @Getter isKioskPlatform;
  @Getter isWebPlatform;
  @Getter orderItemCompleted;
  @Getter orderItemId;
  @Getter order;
  @Getter isGuest;
  @Getter guestAccountData;
  @Getter getPreparedItems;
  @Getter getNotAuthUserData;

  /* Window width starting value */
  windowWidth = window.innerWidth;

  /* Cart starting value */
  cartHeight = window.innerHeight - 70;

  /* Cart navigation drawer permanent*/
  navPermanent = false;

  isDesktopResolution = false;

  toggleCart(value) {
    this.cartDisplay(value);
  }

  /**
   * MOCKED METHOD
   */
  get currentCartItemCompleted() {
    return false;
  }

  get isCheckoutPageOnDesktop() {
    return this.currentPage === 'checkout' && this.isWebPlatform;
  }

  get disabledCart() {
    return this.isCheckoutPageOnDesktop &&
      this.windowWidth <= KIOSK_BREAKPOINT;
  }

  /**
   * Checks the current screen resolution
   */
  checkIfDesktopResolution() {
    const documentWidth = document.documentElement.clientWidth;

    this.isDesktopResolution = documentWidth >= DESKTOP_RESOLUTION;
  }

  created() {
    this.calcOrder = debounce(this.calcOrder, DEBOUNCE_ORDER_QUANTITY_CALC);
  }

  /**
   * Listens window width change
   * and updates isFullscreen result if necessary
   */
  mounted() {
    this.checkIfDesktopResolution();

    this.$nextTick(() => {
      window.addEventListener('resize', () => {
        this.checkIfDesktopResolution();
      });
    });

    window.addEventListener('resize', this.getNavWidth);
    window.addEventListener('resize', this.getNavHeight);
    document.addEventListener('click', (e) => {
      const classList = Object.values(e.target.classList);

      if (classList.length && classList.includes('v-overlay')) {
        this.setNavPermanent(false);
        this.toggleCart(false);
      }
    });
  }

  beforeDestroy() {
    window.removeEventListener('resize', this.getNavWidth);
    window.removeEventListener('resize', this.getNavHeight);
  }

  /**
   * Gets window width and returns 340 for desktop screens,
   * or 810 for kiosk screen,
   * or window width itself for smaller screens
   * where navigation-drawer width should be fullscreen
   */
  getNavWidth() {
    this.windowWidth = window.innerWidth;
    if (this.windowWidth > KIOSK_BREAKPOINT) {
      return 340;
    } else if (this.windowWidth === KIOSK_BREAKPOINT) {
      return 810;
    }

    return this.windowWidth;
  }

  /**
   * Updates full height of the nav sidebar
   */
  getNavHeight() {
    if (process.env.PLATFORM === 'kiosk') {
      this.ordersHeight = window.innerHeight;
    } else {
      this.ordersHeight = window.innerHeight - 70;
    }

    return this.ordersHeight;
  }

  /**
   * Handler to this.idItemForRemove change the quantity of items in the Cart.
   *
   * If you try to reduce the quantity to zero, displays a confirmation modal
   */
  changeQuantity(itemId, quantity) {
    if (quantity === 0) {
      this.setNavPermanent(true);

      this.modalUpdate({
        name: 'cartItemRemove',
        data: itemId,
      });

      return;
    }

    this.cartItemSetQuantity({
      itemId,
      quantity,
    });

    this.calcOrder();
  }

  /**
   * Calc order with debounce.
   */
  async calcOrder() {
    try {
      if (this.order && this.order.promotionCodeApplied && this.isKioskPlatform) {
        const promoCode = this.order.promotionCodeApplied;
        const tempUserData =
          this.authUser ||
          this.getNotAuthUserData ||
          this.guestAccountData;
        const order = {
          restaurant_id:  this.restaurantId,
          pickup_type:    this.orderType,
          first_name:     tempUserData.firstName,
          last_name:      tempUserData.lastName,
          phone:          tempUserData.phone,
          email:          tempUserData.email,
          items:          this.getPreparedItems,
          promotion_code: promoCode,
        };

        await this.cartCalcOrder({ promoCode });
        await this.updateOrder(order);
      } else {
        await this.cartCalcOrder();
      }
    } catch (error) {
      this.setApiError({ detail: error.response.data.detail });
      throw new Error(error);
    }
  }

  /**
   * Handler for "edit item" event
   */
  handleEditItem(itemId) {
    const currentItem = itemId === this.orderItemId;

    if (currentItem) {
      this.cartDisplay(false);

      return;
    }

    if (this.orderItemCompleted) {
      this.clearConfigurator();
      this.startEditItem(itemId);

      return;
    }

    if (this.isKioskPlatform) {
      this.toggleCart(false);
    }

    this.modalUpdate({
      name: 'cartItemEdit',
      data: itemId,
    });
  }

  startEditItem(itemId) {
    const item = this.cart[itemId];

    this.setCurrentProduct({
      productKey:  item.productKey,
      orderItemId: itemId,
      orderItem:   item.data,
    });

    this.modalUpdate({
      name: 'cartItemEdit',
    });

    this.cartDisplay(false);
    this.$router.push({ name: 'configurator', params: { id: item.productKey } });
  }

  /**
   * Handle "Add item" button in the kiosk
   */
  handleAddItemFromKiosk() {
    this.cartDisplay(false);

    if (this.orderItemCompleted) {
      this.$router.push({ name: 'menu' });
      this.modalUpdate({ name: 'goToMenuOrMain' });

      return;
    }

    this.modalUpdate({
      name: 'goToMenuOrMain',
      data: 'menu',
    });
  }

  /**
   * Handle "Add item" button
   */
  handleAddItem() {
    if (this.isKioskPlatform) {
      this.handleAddItemFromKiosk();

      return;
    }

    if (this.orderItemCompleted) {
      this.addNewItem();

      return;
    }

    this.modalUpdate({
      name: 'cartItemAdd',
      data: true,
    });
  }

  /**
   * Redirect to menu, for adding new item to card
   */
  addNewItem() {
    this.clearConfigurator();
    this.$router.push({ name: 'menu' });
    this.modalUpdate({
      name: 'cartItemAdd',
    });
    this.cartDisplay(false);
  }

  /**
   * Handle "Start Over" event
   */
  handleStartOver() {
    if (this.isKioskPlatform) {
      this.cartDisplay(false);
    }

    this.modalUpdate({
      name: 'cartOrderCancel',
      data: true,
    });
  }

  checkout(payload) {
    this.cartDisplay(false);
    this.$emit('checkout', payload);
  }

  setNavPermanent(value) {
    this.navPermanent = value;
  }
}
</script>

<style lang="scss" module>
@import "~@/style/variables.scss";
@import "~@/style/helpers.scss";

.cartTitle {
  display: none;

  .cartTitleLogo {
    max-width: 167px;
    margin: 16px auto 68px;
  }
}

.cartMainTitle {
  @extend %main-title;
  font-size: 48px;
  line-height: 44px;
  text-transform: uppercase;
  margin-bottom: 60px;

  span {
    max-width: 480px;
  }

  span:before,
  span:after {
    width: 90px;
    border-top: 7px solid $green-color;
    top: calc(50% - 5px);
  }

  span:before {
    left: -120px;
  }

  span:after {
    right: -120px;
  }
}

.cartContent .cartTitle .closeDialog {
  position: absolute;
  top: 65px;
  left: 33px;
  padding: 36px 34px;
  border-radius: 50px;

  .backIcon {
    fill: $black-slightly-transparent;
    width: 24px;
    height: 24px;
  }

  span {
    padding-left: 16px;
  }

  &:hover,
  &:focus,
  &:active {
    position: absolute;
  }
}

.pastOrdersClose {
  width: 100%;
  box-shadow: 0 1px 2px 0 rgba(125, 125, 125, 0.37);
  padding: 13px;
  font-family: 'Dekers', sans-serif;
  font-size: 15px;
  font-weight: 700;
  line-height: 18px;
  letter-spacing: -0.08px;
  position: relative;
  background-color: $white;
  outline: none;

  span {
    position: absolute;
    right: 19px;
    top: calc(50% - 5px);
  }
}

.chevronDownIcon {
  fill: $black;
  width: 12px;
  height: 12px;
  transform: rotateX(180deg);
}

.currentOrderCart {
  display: flex;
  flex-flow: column nowrap;
}

.currentOrderItems {
  list-style: none;
  padding-left: 0;
  margin-bottom: 0;
  overflow-y: auto;
}

@media screen and (max-width: $kiosk-breakpoint-max) {
  .cartTitle {
    display: block;
  }
  .pastOrdersClose {
    display: none;
  }
}

@media screen and (max-width: 767px) {
  .cartTitle {
    display: none;
  }
}
</style>



// WEBPACK FOOTER //
// src/apps/cart/components/Cart.vue